import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import BackToTop from '@/components/feature/BackToTop';
import Seo from '@/components/feature/Seo';
import ContactForm from '@/pages/about/components/ContactForm';
import { site, principles, testimonials } from '@/mocks/site';

const aboutJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于反差现场',
    description:
      '反差现场是一个聚焦网红台前幕后反差的中文内容站，介绍网站定位、内容原则与投稿方式。',
    inLanguage: 'zh-CN',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '反差现场',
    description:
      '聚焦网红反差的内容平台，集中呈现直播录制、台下偶遇、粉丝爆料与私下被拍的现场素材。',
    slogan: '台上台下，一镜到底',
    email: site.email,
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: '/' },
      { '@type': 'ListItem', position: 2, name: '关于我们', item: '/about' },
    ],
  },
];

export default function AboutPage() {
  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.slice(1);
      const el = document.getElementById(id);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 120);
      }
    } else {
      window.scrollTo({ top: 0 });
    }
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Seo
        title="关于我们｜反差现场 - 我们如何整理网红反差内容"
        description="了解反差现场的网站定位、内容原则与投稿方式。我们只呈现公开场景与完整语境，守住隐私边界，把镜头前后的真实状态摊开给读者，而不是做单向的道德审判。"
        keywords="反差现场,关于我们,网红反差内容站,反差内容原则,投稿爆料"
        path="/about"
        jsonLd={aboutJsonLd}
      />
      <SiteHeader />

      <main className="flex-1 pt-16 md:pt-20">
        <div className="bg-background-100/60 border-b border-background-200">
          <div className="mx-auto max-w-content px-4 md:px-8 py-8 md:py-12">
            <nav aria-label="面包屑" className="flex items-center flex-wrap gap-2 text-xs text-foreground-500 font-label">
              <Link to="/" className="hover:text-primary-600">首页</Link>
              <i className="ri-arrow-right-s-line" aria-hidden="true" />
              <span className="text-foreground-800">关于我们</span>
            </nav>
            <h1 className="mt-5 font-heading text-3xl md:text-5xl font-black text-foreground-950">
              关于反差现场
            </h1>
            <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-600 max-w-3xl">
              {site.slogan}。我们把镜头前后的现场摊开，让你自己判断，而不是替你先下结论。
            </p>
          </div>
        </div>

        <section className="mx-auto max-w-content px-4 md:px-8 py-12 md:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <p className="text-xs tracking-[0.3em] text-primary-600 font-label">WHO WE ARE</p>
              <h2 className="mt-3 font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                我们是谁
              </h2>
            </div>
            <div className="lg:col-span-7 space-y-5">
              <p className="text-sm md:text-[15px] leading-loose text-foreground-700">
                {site.name}做过的，其实是一件很朴素的事：把那些散落在直播回放、路边监控、粉丝自述里的现场素材，整理成可以被完整阅读的内容。我们相信，反差本身不是结论，它只是一个起点——真正有价值的部分，是你在看到两种状态之后，自己对"人设"与"真实"的理解。
              </p>
              <p className="text-sm md:text-[15px] leading-loose text-foreground-700">
                我们不生产标签，也不替谁定性。站内每一篇档案都尽量交代素材的来源、时间与前后情境，把判断权交还给读者。这也是为什么，我们宁愿把篇幅花在现场还原上，也不愿意用一句耸动的标题替你思考。
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4">
                {[
                  { icon: 'ri-focus-3-line', label: '看现场', desc: '用素材本身说话' },
                  { icon: 'ri-layout-2-line', label: '做对比', desc: '台上台下并列呈现' },
                  { icon: 'ri-lock-2-line', label: '守边界', desc: '隐私内容一律不收录' },
                ].map((item) => (
                  <div key={item.label} className="bg-background-100 border border-background-200 rounded-lg p-4">
                    <span className="w-9 h-9 rounded-md bg-primary-100 text-primary-700 flex items-center justify-center">
                      <i className={`${item.icon} text-lg leading-none`} aria-hidden="true" />
                    </span>
                    <p className="mt-3 font-heading text-base text-foreground-950">{item.label}</p>
                    <p className="mt-1 text-xs text-foreground-500">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="principles" className="bg-background-100/70 border-y border-background-200 scroll-mt-24">
          <div className="mx-auto max-w-content px-4 md:px-8 py-12 md:py-16">
            <div className="mb-8 md:mb-10">
              <p className="text-xs tracking-[0.3em] text-secondary-600 font-label">PRINCIPLES</p>
              <h2 className="mt-3 font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                内容原则
              </h2>
              <p className="mt-2 text-sm text-foreground-600 max-w-2xl">
                这四条是我们筛选、整理与发布内容时的底线，也是我们判断一条素材是否值得被放上来的标准。
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {principles.map((item, index) => (
                <div
                  key={item.title}
                  className="bg-background-50 border border-background-200 rounded-lg p-6 flex gap-4"
                >
                  <span className="w-11 h-11 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center shrink-0 font-heading text-lg font-bold">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <div>
                    <h3 className="font-heading text-lg font-bold text-foreground-950 flex items-center gap-2">
                      <i className={`${item.icon} text-primary-500 text-base`} aria-hidden="true" />
                      {item.title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-content px-4 md:px-8 py-12 md:py-16">
          <div className="mb-8">
            <p className="text-xs tracking-[0.3em] text-primary-600 font-label">REPUTATION</p>
            <h2 className="mt-3 font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              读者口碑
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {testimonials.map((item) => (
              <figure key={item.name} className="bg-background-50 border border-background-200 rounded-lg p-6">
                <div className="flex items-center gap-1 text-accent-500 mb-3" aria-hidden="true">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-sm leading-none" />
                  ))}
                </div>
                <blockquote className="text-sm md:text-[15px] leading-relaxed text-foreground-700">
                  {item.text}
                </blockquote>
                <figcaption className="mt-4 text-xs text-foreground-500 font-label">
                  {item.name} · {item.role}
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section id="contact" className="bg-background-100/70 border-t border-background-200 scroll-mt-24">
          <div className="mx-auto max-w-content px-4 md:px-8 py-12 md:py-16">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
              <div className="lg:col-span-5">
                <p className="text-xs tracking-[0.3em] text-primary-600 font-label">CONTACT</p>
                <h2 className="mt-3 font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                  联系与投稿
                </h2>
                <p className="mt-4 text-sm leading-relaxed text-foreground-600">
                  如果你在现场看到过与公开人设明显不符的场景，欢迎把它讲给我们听。我们不接收跟踪、蹲守所得的素材，也不受理涉及私人信息的请求。
                </p>
                <ul className="mt-6 space-y-4 text-sm">
                  <li className="flex items-center gap-3 text-foreground-700">
                    <span className="w-9 h-9 rounded-md bg-secondary-100 text-secondary-700 flex items-center justify-center shrink-0">
                      <i className="ri-mail-line text-base leading-none" aria-hidden="true" />
                    </span>
                    <a href={`mailto:${site.email}`} className="hover:text-primary-600 transition-colors">
                      {site.email}
                    </a>
                  </li>
                  <li className="flex items-center gap-3 text-foreground-700">
                    <span className="w-9 h-9 rounded-md bg-secondary-100 text-secondary-700 flex items-center justify-center shrink-0">
                      <i className="ri-time-line text-base leading-none" aria-hidden="true" />
                    </span>
                    工作日 10:00 - 18:00 内回复
                  </li>
                  <li className="flex items-center gap-3 text-foreground-700">
                    <span className="w-9 h-9 rounded-md bg-secondary-100 text-secondary-700 flex items-center justify-center shrink-0">
                      <i className="ri-shield-check-line text-base leading-none" aria-hidden="true" />
                    </span>
                    投稿信息仅用于内容核实
                  </li>
                </ul>
              </div>
              <div className="lg:col-span-7">
                <ContactForm />
              </div>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
      <BackToTop />
    </div>
  );
}